import React, { useState, Children } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MapPin, Star, Heart, Share2, ChevronLeft, ChevronRight, ExternalLink, Clock, Check, Plane, Building2, ShoppingBag, Landmark, Navigation, Users, BedDouble } from 'lucide-react';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
// Extended hotel data with full details
interface RoomType {
  id: string;
  name: string;
  description: string;
  size: string;
  beds: string;
  maxGuests: number;
  price: number;
  images: string[];
  amenities: string[];
  available: boolean;
}
interface HotelDetail {
  id: string;
  name: string;
  location: string;
  address: string;
  rating: number;
  reviews: number;
  images: string[];
  description: string;
  coordinates: {
    lat: number;
    lng: number;
  };
  distanceFromAirport: string;
  checkIn: string;
  checkOut: string;
  cancellationPolicy: string;
  amenities: {
    category: string;
    items: string[];
  }[];
  rooms: RoomType[];
  nearbyLocations: {
    name: string;
    distance: string;
    type: 'attraction' | 'shopping' | 'transport' | 'landmark';
  }[];
  policies: string[];
}
// Sample hotel data
const hotelData: Record<string, HotelDetail> = {
  d1: {
    id: 'd1',
    name: 'Burj Al Arab Jumeirah',
    location: 'Dubai, UAE',
    address: 'Jumeirah Street, Umm Suqeim 3, Dubai, United Arab Emirates',
    rating: 4.9,
    reviews: 2847,
    images: ['https://images.unsplash.com/photo-1582719508461-905c673771fd?w=1200&q=80', 'https://images.unsplash.com/photo-1566073771259-6a8506099945?w=1200&q=80', 'https://images.unsplash.com/photo-1571896349842-33c89424de2d?w=1200&q=80', 'https://images.unsplash.com/photo-1564501049412-61c2a3083791?w=1200&q=80', 'https://images.unsplash.com/photo-1551882547-ff40c63fe5fa?w=1200&q=80'],
    description: "Standing on its own man-made island, Burj Al Arab Jumeirah is one of the world's most luxurious hotels. This iconic sail-shaped building offers unparalleled opulence with its lavish suites, world-class dining, and exceptional service. Each suite features floor-to-ceiling windows with stunning views of the Arabian Gulf, along with personalized butler service available 24/7.",
    coordinates: {
      lat: 25.1412,
      lng: 55.1853
    },
    distanceFromAirport: '25 km (30 min drive)',
    checkIn: '3:00 PM',
    checkOut: '12:00 PM',
    cancellationPolicy: 'Free cancellation up to 48 hours before check-in. After that, one night will be charged.',
    amenities: [{
      category: 'General',
      items: ['Free WiFi', '24-Hour Front Desk', 'Concierge Service', 'Valet Parking', 'Currency Exchange', 'Luggage Storage']
    }, {
      category: 'Dining',
      items: ['9 Restaurants', 'Room Service 24/7', 'Bar/Lounge', 'Private Dining', 'In-Suite Dining', 'Afternoon Tea']
    }, {
      category: 'Wellness',
      items: ['Talise Spa', 'Fitness Center', 'Infinity Pool', 'Private Beach', 'Sauna', 'Steam Room', 'Jacuzzi']
    }, {
      category: 'Services',
      items: ['Butler Service', 'Chauffeur Service', 'Helicopter Transfers', 'Yacht Charter', 'Personal Shopping', 'Babysitting']
    }, {
      category: 'Business',
      items: ['Meeting Rooms', 'Business Center', 'Conference Facilities', 'High-Speed Internet', 'Secretarial Services']
    }],
    rooms: [{
      id: 'r1',
      name: 'Deluxe One Bedroom Suite',
      description: 'Luxurious two-floor suite with panoramic views of the Arabian Gulf',
      size: '170 sqm',
      beds: '1 King Bed',
      maxGuests: 3,
      price: 1850,
      images: ['https://images.unsplash.com/photo-1590490360182-c33d57733427?w=800&q=80', 'https://images.unsplash.com/photo-1584132967334-10e028bd69f7?w=800&q=80'],
      amenities: ['Butler Service', 'Sea View', 'Separate Living Room', 'Marble Bathroom', 'Pillow Menu', 'Hermes Amenities'],
      available: true
    }, {
      id: 'r2',
      name: 'Panoramic Suite',
      description: 'Spectacular suite with floor-to-ceiling windows and stunning sea views',
      size: '200 sqm',
      beds: '1 King Bed',
      maxGuests: 3,
      price: 2400,
      images: ['https://images.unsplash.com/photo-1566665797739-1674de7a421a?w=800&q=80', 'https://images.unsplash.com/photo-1582719478250-c89cae4dc85b?w=800&q=80'],
      amenities: ['Butler Service', 'Panoramic Views', 'Dining Area', 'Walk-in Closet', 'Rain Shower', 'Separate Powder Room'],
      available: true
    }, {
      id: 'r3',
      name: 'Club Suite',
      description: 'Exclusive suite with access to Club Lounge and private check-in',
      size: '220 sqm',
      beds: '1 King Bed + Sofa Bed',
      maxGuests: 4,
      price: 3200,
      images: ['https://images.unsplash.com/photo-1631049307264-da0ec9d70304?w=800&q=80', 'https://images.unsplash.com/photo-1618773928121-c32242e63f39?w=800&q=80'],
      amenities: ['Club Lounge Access', 'Private Check-in', 'Complimentary Breakfast', 'Evening Cocktails', 'Meeting Room Access'],
      available: false
    }, {
      id: 'r4',
      name: 'Royal Suite',
      description: 'Ultimate luxury with private cinema, library, and dedicated butler',
      size: '780 sqm',
      beds: '2 King Beds',
      maxGuests: 6,
      price: 8500,
      images: ['https://images.unsplash.com/photo-1578683010236-d716f9a3f461?w=800&q=80', 'https://images.unsplash.com/photo-1595576508898-0ad5c879a061?w=800&q=80'],
      amenities: ['Private Cinema', 'Library', 'Dining Room', 'Private Elevator', 'Rotating Bed', 'Gold iPad'],
      available: true
    }],
    nearbyLocations: [{
      name: 'Dubai Mall',
      distance: '8 km',
      type: 'shopping'
    }, {
      name: 'Burj Khalifa',
      distance: '9 km',
      type: 'landmark'
    }, {
      name: 'Palm Jumeirah',
      distance: '4 km',
      type: 'attraction'
    }, {
      name: 'Mall of the Emirates',
      distance: '6 km',
      type: 'shopping'
    }, {
      name: 'Dubai Marina',
      distance: '12 km',
      type: 'attraction'
    }, {
      name: 'Dubai International Airport',
      distance: '25 km',
      type: 'transport'
    }, {
      name: 'Jumeirah Beach',
      distance: '1 km',
      type: 'attraction'
    }, {
      name: 'Ski Dubai',
      distance: '6 km',
      type: 'attraction'
    }],
    policies: ['Check-in time is 3:00 PM and check-out time is 12:00 PM', 'Early check-in and late check-out are subject to availability', 'Children of all ages are welcome', 'Extra beds are available upon request', 'Pets are not allowed', 'Smoking is not permitted in suites', 'Valid ID and credit card required at check-in', 'Dress code applies in certain restaurants']
  }
};
interface HotelDetailPageProps {
  hotelId?: string;
}
export function HotelDetailPage({
  hotelId = 'd1'
}: HotelDetailPageProps) {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const hotel = hotelData[hotelId] || hotelData['d1'];
  const nextImage = () => {
    setCurrentImageIndex(prev => (prev + 1) % hotel.images.length);
  };
  const prevImage = () => {
    setCurrentImageIndex(prev => (prev - 1 + hotel.images.length) % hotel.images.length);
  };
  const getGoogleMapsUrl = () => {
    return `https://www.google.com/maps/search/?api=1&query=${hotel.coordinates.lat},${hotel.coordinates.lng}`;
  };
  const getLocationIcon = (type: string) => {
    switch (type) {
      case 'shopping':
        return ShoppingBag;
      case 'transport':
        return Plane;
      case 'landmark':
        return Landmark;
      default:
        return Building2;
    }
  };
  const handleBookRoom = (roomId: string) => {
    // Navigate to booking page with hotel and room data
    window.location.hash = `booking-${hotelId}-${roomId}`;
  };
  return <div className="min-h-screen bg-gray-50" data-id="element-797">
      <Header data-id="element-798" />

      {/* Image Gallery */}
      <section className="relative bg-black" data-id="element-799">
        <div className="relative h-[400px] md:h-[500px] lg:h-[600px]" data-id="element-800">
          <AnimatePresence mode="wait" data-id="element-801">
            <motion.img key={currentImageIndex} src={hotel.images[currentImageIndex]} alt={`${hotel.name} - Image ${currentImageIndex + 1}`} className="absolute inset-0 w-full h-full object-cover" initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} exit={{
            opacity: 0
          }} transition={{
            duration: 0.5
          }} data-id="element-802" />
          </AnimatePresence>

          {/* Gallery Navigation */}
          <div className="absolute inset-0 flex items-center justify-between px-4" data-id="element-803">
            <button onClick={prevImage} className="p-3 rounded-full bg-white/90 text-gray-900 hover:bg-white transition-colors shadow-lg backdrop-blur-sm" aria-label="Previous image" data-id="element-804">
              <ChevronLeft className="w-6 h-6" data-id="element-805" />
            </button>
            <button onClick={nextImage} className="p-3 rounded-full bg-white/90 text-gray-900 hover:bg-white transition-colors shadow-lg backdrop-blur-sm" aria-label="Next image" data-id="element-806">
              <ChevronRight className="w-6 h-6" data-id="element-807" />
            </button>
          </div>

          {/* Image Counter */}
          <div className="absolute bottom-6 left-1/2 -translate-x-1/2 px-4 py-2 rounded-full bg-black/60 text-white text-sm backdrop-blur-sm" data-id="element-808">
            {currentImageIndex + 1} / {hotel.images.length}
          </div>

          {/* Action Buttons */}
          <div className="absolute top-6 right-6 flex gap-3" data-id="element-809">
            <button className="p-3 rounded-full bg-white/90 text-gray-900 hover:bg-white transition-colors shadow-lg backdrop-blur-sm" data-id="element-810">
              <Share2 className="w-5 h-5" data-id="element-811" />
            </button>
            <button className="p-3 rounded-full bg-white/90 text-gray-900 hover:text-rose-500 hover:bg-white transition-colors shadow-lg backdrop-blur-sm" data-id="element-812">
              <Heart className="w-5 h-5" data-id="element-813" />
            </button>
          </div>
        </div>

        {/* Thumbnail Strip */}
        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-6" data-id="element-814">
          <div className="max-w-7xl mx-auto flex gap-2 overflow-x-auto" data-id="element-815">
            {hotel.images.map((img, index) => <button key={index} onClick={() => setCurrentImageIndex(index)} className={`relative flex-shrink-0 w-20 h-20 rounded-lg overflow-hidden border-2 transition-all ${index === currentImageIndex ? 'border-white scale-105' : 'border-transparent opacity-60 hover:opacity-100'}`} data-id="element-816">
                <img src={img} alt={`Thumbnail ${index + 1}`} className="w-full h-full object-cover" data-id="element-817" />
              </button>)}
          </div>
        </div>
      </section>

      {/* Hotel Info Header */}
      <section className="bg-white border-b border-gray-200 sticky top-16 z-40" data-id="element-818">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6" data-id="element-819">
          <div className="flex flex-col gap-4" data-id="element-820">
            <div className="flex-1" data-id="element-821">
              <div className="flex items-center gap-2 mb-2" data-id="element-822">
                <a href="#" onClick={e => {
                e.preventDefault();
                window.history.back();
              }} className="text-travel-blue-600 hover:text-travel-blue-700 flex items-center gap-1 text-sm font-medium" data-id="element-823">
                  <ChevronLeft className="w-4 h-4" data-id="element-824" />
                  Back
                </a>
              </div>
              <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2" data-id="element-825">
                {hotel.name}
              </h1>
              <div className="flex flex-wrap items-center gap-4 text-sm" data-id="element-826">
                <div className="flex items-center gap-1" data-id="element-827">
                  <div className="flex items-center bg-amber-50 px-2 py-1 rounded-lg" data-id="element-828">
                    <Star className="w-4 h-4 text-amber-500 fill-amber-500 mr-1" data-id="element-829" />
                    <span className="font-bold text-amber-700" data-id="element-830">
                      {hotel.rating}
                    </span>
                  </div>
                  <span className="text-gray-600" data-id="element-831">
                    ({hotel.reviews.toLocaleString()} reviews)
                  </span>
                </div>
                <div className="flex items-center gap-1 text-gray-600" data-id="element-832">
                  <MapPin className="w-4 h-4" data-id="element-833" />
                  <span data-id="element-834">{hotel.location}</span>
                </div>
                <a href={getGoogleMapsUrl()} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-travel-blue-600 hover:text-travel-blue-700 font-medium" data-id="element-835">
                  <Navigation className="w-4 h-4" data-id="element-836" />
                  View on Map
                  <ExternalLink className="w-3 h-3" data-id="element-837" />
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content - Linear Flow */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-8" data-id="element-838">
        {/* Overview Section */}
        <motion.section initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.5
      }} className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 lg:p-8" data-id="element-839">
          <h2 className="text-2xl font-bold text-gray-900 mb-6" data-id="element-840">
            About This Hotel
          </h2>
          <p className="text-gray-600 leading-relaxed mb-8 text-lg" data-id="element-841">
            {hotel.description}
          </p>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-8" data-id="element-842">
            <div className="flex items-start gap-3 p-4 bg-gray-50 rounded-xl" data-id="element-843">
              <Clock className="w-5 h-5 text-travel-blue-600 flex-shrink-0 mt-0.5" data-id="element-844" />
              <div data-id="element-845">
                <p className="font-semibold text-gray-900 mb-1" data-id="element-846">
                  Check-in / Check-out
                </p>
                <p className="text-sm text-gray-600" data-id="element-847">
                  {hotel.checkIn} / {hotel.checkOut}
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3 p-4 bg-gray-50 rounded-xl" data-id="element-848">
              <Plane className="w-5 h-5 text-travel-blue-600 flex-shrink-0 mt-0.5" data-id="element-849" />
              <div data-id="element-850">
                <p className="font-semibold text-gray-900 mb-1" data-id="element-851">
                  Airport Distance
                </p>
                <p className="text-sm text-gray-600" data-id="element-852">
                  {hotel.distanceFromAirport}
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3 p-4 bg-gray-50 rounded-xl" data-id="element-853">
              <MapPin className="w-5 h-5 text-travel-blue-600 flex-shrink-0 mt-0.5" data-id="element-854" />
              <div data-id="element-855">
                <p className="font-semibold text-gray-900 mb-1" data-id="element-856">Location</p>
                <p className="text-sm text-gray-600" data-id="element-857">{hotel.location}</p>
              </div>
            </div>
          </div>

          <div className="border-t border-gray-200 pt-8" data-id="element-858">
            <h3 className="text-xl font-bold text-gray-900 mb-4" data-id="element-859">
              Hotel Policies
            </h3>
            <div className="grid sm:grid-cols-2 gap-3" data-id="element-860">
              {hotel.policies.map((policy, index) => <div key={index} className="flex items-start gap-2 text-sm text-gray-600" data-id="element-861">
                  <Check className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" data-id="element-862" />
                  <span data-id="element-863">{policy}</span>
                </div>)}
            </div>
          </div>

          <div className="border-t border-gray-200 pt-8 mt-8" data-id="element-864">
            <h3 className="text-xl font-bold text-gray-900 mb-3" data-id="element-865">
              Cancellation Policy
            </h3>
            <p className="text-gray-600 leading-relaxed" data-id="element-866">
              {hotel.cancellationPolicy}
            </p>
          </div>
        </motion.section>

        {/* Rooms Section */}
        <motion.section initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.5,
        delay: 0.1
      }} className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 lg:p-8" data-id="element-867">
          <h2 className="text-2xl font-bold text-gray-900 mb-6" data-id="element-868">
            Available Rooms
          </h2>
          <div className="space-y-6" data-id="element-869">
            {hotel.rooms.map((room, index) => <motion.div key={room.id} initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.3,
            delay: index * 0.1
          }} className="border border-gray-200 rounded-xl overflow-hidden hover:border-travel-blue-300 transition-colors" data-id="element-870">
                <div className="grid md:grid-cols-3 gap-6 p-6" data-id="element-871">
                  <div className="relative aspect-[4/3] rounded-lg overflow-hidden" data-id="element-872">
                    <img src={room.images[0]} alt={room.name} className="w-full h-full object-cover" data-id="element-873" />
                    {!room.available && <div className="absolute inset-0 bg-black/60 flex items-center justify-center" data-id="element-874">
                        <span className="px-3 py-1 bg-red-500 text-white text-sm font-semibold rounded-full" data-id="element-875">
                          Sold Out
                        </span>
                      </div>}
                  </div>

                  <div className="md:col-span-2" data-id="element-876">
                    <div className="flex items-start justify-between mb-3" data-id="element-877">
                      <div data-id="element-878">
                        <h3 className="text-xl font-bold text-gray-900 mb-2" data-id="element-879">
                          {room.name}
                        </h3>
                        <p className="text-gray-600 mb-4" data-id="element-880">{room.description}</p>
                      </div>
                    </div>

                    <div className="flex flex-wrap gap-6 mb-4 text-sm text-gray-600" data-id="element-881">
                      <div className="flex items-center gap-2" data-id="element-882">
                        <BedDouble className="w-5 h-5 text-travel-blue-600" data-id="element-883" />
                        <span className="font-medium" data-id="element-884">{room.beds}</span>
                      </div>
                      <div className="flex items-center gap-2" data-id="element-885">
                        <Users className="w-5 h-5 text-travel-blue-600" data-id="element-886" />
                        <span className="font-medium" data-id="element-887">
                          Up to {room.maxGuests} guests
                        </span>
                      </div>
                      <div className="flex items-center gap-2" data-id="element-888">
                        <Building2 className="w-5 h-5 text-travel-blue-600" data-id="element-889" />
                        <span className="font-medium" data-id="element-890">{room.size}</span>
                      </div>
                    </div>

                    <div className="flex flex-wrap gap-2 mb-6" data-id="element-891">
                      {room.amenities.map(amenity => <span key={amenity} className="text-xs px-3 py-1 bg-travel-blue-50 text-travel-blue-700 rounded-full border border-travel-blue-100" data-id="element-892">
                          {amenity}
                        </span>)}
                    </div>

                    <div className="flex items-center justify-between pt-4 border-t border-gray-200" data-id="element-893">
                      <div data-id="element-894">
                        <p className="text-sm text-gray-500 mb-1" data-id="element-895">
                          Price per night
                        </p>
                        <p className="text-3xl font-bold text-gray-900" data-id="element-896">
                          ${room.price}
                        </p>
                      </div>
                      <button disabled={!room.available} onClick={() => room.available && handleBookRoom(room.id)} className={`px-8 py-3 rounded-xl font-semibold transition-colors ${room.available ? 'bg-travel-blue-600 text-white hover:bg-travel-blue-700 shadow-lg shadow-travel-blue-600/20' : 'bg-gray-200 text-gray-500 cursor-not-allowed'}`} data-id="element-897">
                        {room.available ? 'Book Now' : 'Not Available'}
                      </button>
                    </div>
                  </div>
                </div>
              </motion.div>)}
          </div>
        </motion.section>

        {/* Amenities Section */}
        <motion.section initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.5,
        delay: 0.2
      }} className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 lg:p-8" data-id="element-898">
          <h2 className="text-2xl font-bold text-gray-900 mb-6" data-id="element-899">
            Hotel Amenities
          </h2>
          <div className="space-y-8" data-id="element-900">
            {hotel.amenities.map(category => <div key={category.category} data-id="element-901">
                <h3 className="text-xl font-bold text-gray-900 mb-4" data-id="element-902">
                  {category.category}
                </h3>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-903">
                  {category.items.map(item => <div key={item} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg" data-id="element-904">
                      <Check className="w-5 h-5 text-green-600 flex-shrink-0" data-id="element-905" />
                      <span className="text-gray-700 font-medium" data-id="element-906">{item}</span>
                    </div>)}
                </div>
              </div>)}
          </div>
        </motion.section>

        {/* Location Section */}
        <motion.section initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.5,
        delay: 0.3
      }} className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 lg:p-8" data-id="element-907">
          <h2 className="text-2xl font-bold text-gray-900 mb-6" data-id="element-908">
            Location & Nearby
          </h2>
          <div className="mb-8" data-id="element-909">
            <h3 className="text-lg font-bold text-gray-900 mb-3" data-id="element-910">Address</h3>
            <p className="text-gray-600 mb-4 text-lg" data-id="element-911">{hotel.address}</p>
            <a href={getGoogleMapsUrl()} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-6 py-3 bg-travel-blue-600 text-white rounded-xl hover:bg-travel-blue-700 transition-colors shadow-lg shadow-travel-blue-600/20" data-id="element-912">
              <Navigation className="w-5 h-5" data-id="element-913" />
              Open in Google Maps
              <ExternalLink className="w-4 h-4" data-id="element-914" />
            </a>
          </div>

          <div className="border-t border-gray-200 pt-8" data-id="element-915">
            <h3 className="text-lg font-bold text-gray-900 mb-6" data-id="element-916">
              Nearby Locations
            </h3>
            <div className="grid sm:grid-cols-2 gap-4" data-id="element-917">
              {hotel.nearbyLocations.map((location, index) => {
              const Icon = getLocationIcon(location.type);
              return <div key={index} className="flex items-center justify-between p-4 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors" data-id="element-918">
                    <div className="flex items-center gap-3" data-id="element-919">
                      <div className="p-3 bg-white rounded-lg shadow-sm" data-id="element-920">
                        <Icon className="w-5 h-5 text-travel-blue-600" data-id="element-921" />
                      </div>
                      <span className="font-semibold text-gray-900" data-id="element-922">
                        {location.name}
                      </span>
                    </div>
                    <span className="text-sm font-medium text-gray-600" data-id="element-923">
                      {location.distance}
                    </span>
                  </div>;
            })}
            </div>
          </div>
        </motion.section>
      </div>

      <Footer data-id="element-924" />
    </div>;
}